<template>
  <div
      class="basket-item"
      :class="{ 'basket-item--has-errors': !!itemCalculated?.error }"
      ref="container"
  >
    <div class="row q-col-gutter-sm no-wrap">
      <div class="col-4">
        <div
            v-if="product && product.IMAGE?.SRC"
            @click="onClick"
            class="c-image cursor-pointer"
        >
          <img v-if="product" :src="imageResolve(product.IMAGE?.SRC, 100)"/>
        </div>
      </div>
      <div class="col-20">
        <div class="row q-col-gutter-sm">
          <div class="col-16 col-xl-17">
            <div class="cursor-pointer" @click="onClick">
              <div class="c-info full-width leading-e4">
                <span class="s-font-xs text-weight-bold">{{
                    productName
                  }}</span>
                <div class="c-hovered" v-if="isHovered && children.length">
                  <q-btn
                      flat
                      :icon="ICONS.pencil"
                      color="primary"
                      class="c-edit"
                      dense
                      size="14px"
                  />
                </div>
              </div>
            </div>

            <div class="c-price text-weight-medium leading-none flex q-mt-xs">
              <div
                  class="c-price__value s-font-2xs text-grey-7"
                  v-html="formatPrice(priceWithChildren, true)"
              />
              <div
                  class="price-old s-font-2xs q-ml-sm"
                  v-html="formatPrice(calc.priceSource, true, true)"
                  v-if="calc.discountPercent"
              />
            </div>
          </div>
          <div class="col-8 col-xl-7 text-right">
            <QuantityInput
                :model-value="item.fields.QUANTITY"
                @update:model-value="onQuantityUpdate"
                :btn="{
                  size: '16px',
                  outline: true,
                  color: 'grey-7',
                  class: 'q-px-sm',
                  flat: true,
                  dense: true,
                }"
                :show-action-tooltip="true"
                class="c-quantity"
                control-class=""
                :input-disable="true"
            />
          </div>
        </div>

        <div class="q-mt-sm">
          <div
              v-if="childrenSummary.length"
              class="s-font-xs text-grey-7 cursor-pointer q-mb-xs"
              @click="expanded = !expanded"
          >
            <span
                v-for="(child, index) in childrenSummary"
                :key="child.getUuid()"
                class="q-mr-sm"
            >
              {{ child.fields.NAME }}
              <span class="text-no-wrap">
                <span class="text-weight-bold"
                >x {{ child.fields.QUANTITY }}</span
                >
                <template v-if="index < childrenSummary.length - 1">,</template>
              </span>
            </span>
          </div>

          <div v-if="childrenTree.length" class="q-mt-xs">
            <div class="flex q-gutter-xs">
              <BasketSubItem
                  v-for="subitem in childrenTree"
                  :key="subitem.id"
                  :item="subitem"
              />
            </div>
            <div
                class="cursor-pointer s-font-3xs text-primary q-mt-xs"
                @click="expanded = !expanded"
                v-if="expanded"
            >
              <span>Свернуть</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
        v-if="itemCalculated?.error"
        class="c-errors q-gutter-y-xs s-font-2xs full-width"
    >
      <div class="flex q-gutter-x-sm text-red-8 no-wrap">
        <q-icon
            size="14px"
            :name="ICONS.exclamationCircleSolid"
        />
        <div class="flex-1 leading-e5">
          {{ itemCalculated?.error?.message }}
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">

import {ICONS} from "@/assets/icons";
import QuantityInput from "@/components/Ui/QuantityInput.vue";
import {useImager} from "@/core/store/imager";
import toInt from "@/core/util/toInt";
import {BasketItemModel} from "@/modules/shop/composable/util";
import {useBasketStore} from "@/modules/shop/store/basket";
import {useCurrency} from "@/modules/shop/store/currency";
import {useVorderTotalStore} from "@/modules/shop/store/vorder-total";
import {useElementHover} from "@vueuse/core";
import {computed, ref, toRefs, useTemplateRef} from "vue";
import {useRouter} from "vue-router";
import BasketSubItem from "./BasketSubItem.vue";
import {useDebugComponent} from "@/core/composable/useDebugComponent";

const basketStore = useBasketStore();
const totalStore = useVorderTotalStore();

const router = useRouter();
const {imageResolve} = useImager();
const {formatPrice} = useCurrency();
const {basketOp} = basketStore;

type BasketItemProps = {
  item: BasketItemModel;
};

const props = withDefaults(defineProps<BasketItemProps>(), {});

const {item} = toRefs(props);

const productId = computed(() => item.value.fields.PRODUCT_ID);
const product = computed(() => item.value.product);
const productName = computed(() =>
    product.value ? product.value.NAME : item.value.fields.NAME
);
const children = computed(() => item.value.children.value);
const expanded = ref(false);

const childrenTree = computed(() =>
    children.value.filter(
        (child) => child.getReasonModificator()?.TYPE === "gift" || expanded.value
    )
);

const childrenSummary = computed(() =>
    children.value.filter((child) => {
      if (child.isGift.value || expanded.value) return false;
      return true;
    })
);

const itemCalculated = computed(() => {
  return totalStore.basketItemsCalculatedById[item.value.id];
});

const calc = computed(() => {
  return (
      itemCalculated.value?.calc || {
        price: 0,
        priceSource: 0,
        priceTotal: 0,
        priceTotalSource: 0,
        discountPercent: 0,
      }
  );
});


const priceWithChildren = computed(() => {
  const childrenPrice = children.value
      .map(
          (child) =>
              totalStore.basketItemsCalculatedById[child.id]?.calc?.priceTotal || 0
      )
      .reduce((acc, price) => {
        acc += price;
        return acc;
      }, 0);
  return calc.value.price + childrenPrice;
});

const onClick = () => {
  if (product.value) {
    router.push({
      path: "/product/" + productId.value,
      query: {baskethash: item.value.getUuid()},
    });
  }
};

const onQuantityUpdate = (value: number) => {
  value = toInt(value);
  if (value >= 0) {
    basketOp({
      action: "quantity",
      item: item.value,
      quantity: value,
    });
  }
};

const containerRef = useTemplateRef("container");
const isHovered = useElementHover(containerRef);

const subitems = computed(() => {
});

// Debug композабл для отслеживания ререндеров
useDebugComponent('BasketItem', {collectRenders: true});
</script>

<style scoped lang="scss">
.c-image {
  background-color: #efefef;
  border-radius: 10px;
  line-height: 1;
  font-size: 0px;
}

.basket-item {
  transition: border-left-color 0.2s ease;

  &--has-errors {
    border-left: 3px solid #f44336;
    padding-left: 8px;
  }
}

.c-hovered {
  position: absolute;
  right: 0;
  top: 0;
}

.c-info {
  position: relative;
}

.node-line {
  position: relative;
}

.node-line::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  height: 100%;
  border-left: var(--line-width) solid var(--line-color);
}

.c-price {
  margin-top: 6px;
}

.c-errors {
  .q-icon {
    flex-shrink: 0;
  }
}
</style>
